import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { adminTokens } from '../../admin-ui';
import { type ClientAccount } from '../../api/accountAssignmentApi';
import { useClientContext } from '../../hooks/useClientContext';
import { useClientContextAccounts, useClientContextRouteScope } from './useClientContextScope';
import { createSearchableContextPickerStyles } from './searchableContextPickerStyles';
import { useSearchableContextPicker } from './useSearchableContextPicker';

const EMTRAIN_ID = '__emtrain__';
const EMTRAIN_LABEL = 'Emtrain (Standard)';

/** Display-name convention for a client account, shared by account-scoped surfaces. */
export function getAccountLabel(account: Pick<ClientAccount, 'id' | 'name' | 'subdomain'>): string {
  return account.name || account.subdomain || String(account.id);
}

export function ClientContextPicker() {
  const { clientContext, setClientContext } = useClientContext();
  const { query, isScoped } = useClientContextAccounts();
  const { listKind } = useClientContextRouteScope();
  const {
    data: accounts = [],
    isLoading,
    isError,
    isSuccess,
    isFetching,
    isPlaceholderData,
  } = query;

  const [hoveredId, setHoveredId] = useState<string | number | null>(null);
  const {
    isOpen,
    searchQuery,
    setSearchQuery,
    triggerRef,
    containerRef,
    searchInputRef,
    openDropdown: openPicker,
    closeDropdown: closePicker,
    handleKeyDown,
  } = useSearchableContextPicker();

  // True only when the current key's data is fully settled: loaded, not a background refetch,
  // and not placeholder data from a prior key. Using this as the gate prevents resets during
  // loading, background refetches, and when the global placeholderData override is off.
  const hasCurrentAccounts = isSuccess && !isPlaceholderData && !isFetching;

  // Reset stale selection: if selected client is absent from the current settled account list,
  // revert to null. On detail routes this enforces the deep-link reset against assigned accounts.
  // Skipped on list routes (/lessons, /courses): having zero assigned items there is a valid,
  // expected state — the list itself renders an empty state explaining it — not a sign the
  // selection has become invalid, unlike viewing a specific piece of content the account isn't
  // actually assigned to.
  useEffect(() => {
    if (clientContext !== null && hasCurrentAccounts && listKind === null) {
      const stillPresent = accounts.some((a) => a.id === clientContext.id);
      if (!stillPresent) {
        setClientContext(null);
      }
    }
  }, [accounts, hasCurrentAccounts, clientContext, setClientContext, listKind]);

  const filteredAccounts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return accounts;
    return accounts.filter((account) => {
      return [
        account.name,
        account.id,
        account.externalId ?? '',
        account.subdomain ?? '',
      ].some((value) => value.toLowerCase().includes(normalizedQuery));
    });
  }, [accounts, searchQuery]);

  // Items list: Emtrain pinned first, then filtered client accounts
  const listItems = useMemo(
    () => [{ id: EMTRAIN_ID, label: EMTRAIN_LABEL, isEmtrain: true } as const, ...filteredAccounts.map((a) => ({
      id: a.id,
      label: getAccountLabel(a),
      isEmtrain: false as const,
      account: a,
    }))],
    [filteredAccounts]
  );

  const openDropdown = useCallback(() => {
    setHoveredId(null);
    openPicker();
  }, [openPicker]);

  const closeDropdown = useCallback((returnFocus = true) => {
    setHoveredId(null);
    closePicker(returnFocus);
  }, [closePicker]);

  const selectItem = useCallback((item: typeof listItems[number]) => {
    setClientContext(item.isEmtrain ? null : item.account);
    closeDropdown();
  }, [setClientContext, closeDropdown]);

  const triggerLabel = clientContext === null ? EMTRAIN_LABEL : getAccountLabel(clientContext);

  const styles = useMemo(() => createStyles(), []);

  const dropdownId = 'content-context-listbox';
  const searchId = 'content-context-search';

  return (
    <div style={styles.container} ref={containerRef} onKeyDown={handleKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        onClick={openDropdown}
        style={styles.trigger}
        aria-label={`Content context: ${triggerLabel}`}
        title={triggerLabel}
      >
        <span style={styles.triggerLabel}>{triggerLabel}</span>
        <ChevronDown size={14} strokeWidth={1.75} style={{ flexShrink: 0 }} aria-hidden="true" />
      </button>

      {isOpen && (
        <div style={styles.dropdown} role="dialog" aria-label="Select content context">
          <div style={styles.searchWrapper}>
            <div style={styles.searchIcon} aria-hidden="true">
              <Search size={14} strokeWidth={1.75} aria-hidden="true" />
            </div>
            <input
              ref={searchInputRef}
              id={searchId}
              type="search"
              placeholder="Search accounts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
              aria-label="Search accounts"
              autoComplete="off"
            />
          </div>

          <ul
            id={dropdownId}
            role="listbox"
            aria-label="Content context options"
            style={styles.list}
          >
            {/* Emtrain pinned first, divider, then client accounts */}
            {listItems.map((item, index) => {
              const isSelected = item.isEmtrain
                ? clientContext === null
                : clientContext !== null && clientContext.id === item.id;
              return (
                <Fragment key={item.id}>
                  <li
                    id={`content-context-option-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => selectItem(item)}
                    onMouseEnter={() => setHoveredId(item.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    style={{
                      ...styles.listItem,
                      ...(hoveredId === item.id ? styles.listItemHovered : {}),
                      ...(isSelected ? styles.listItemSelected : {}),
                    }}
                  >
                    <span style={styles.listItemLabel}>{item.label}</span>
                  </li>
                  {item.isEmtrain && (
                    <li role="separator" style={styles.divider} aria-hidden="true" />
                  )}
                </Fragment>
              );
            })}

            {/* Status rows shown in place of the client account list */}
            {isLoading && (
              <li style={styles.statusItem} role="status">
                Loading accounts...
              </li>
            )}
            {isError && !isLoading && (
              <li style={styles.statusItem} role="alert">
                {isScoped ? 'Could not load assigned accounts.' : 'Could not load client accounts.'}
              </li>
            )}
            {!isLoading && !isError && filteredAccounts.length === 0 && (searchQuery.trim() || !isScoped) && (
              <li style={styles.statusItem}>
                {searchQuery.trim() ? 'No accounts match your search.' : 'No client accounts available.'}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function createStyles(): Record<string, CSSProperties> {
  const base = createSearchableContextPickerStyles({
    triggerMinWidth: '190px',
    triggerMaxWidth: '280px',
    dropdownWidth: '280px',
    listMaxHeight: '260px',
  });
  return {
    ...base,
    listItem: {
      display: 'flex',
      alignItems: 'center',
      padding: `${adminTokens.spacing.sm} ${adminTokens.spacing.md}`,
      borderRadius: adminTokens.radius.control,
      cursor: 'pointer',
      fontSize: adminTokens.typography.body.size,
      color: adminTokens.text.primary,
      userSelect: 'none',
    },
    listItemLabel: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    divider: {
      height: '1px',
      margin: `${adminTokens.spacing.xs} 0`,
      backgroundColor: adminTokens.border.subtle,
    },
  };
}
